import { useCallback, useEffect, useState } from 'react'
import { Lock, X } from 'lucide-react'
import { Link } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { useCheckout, usePlans, useSubscription } from '@/api/subscription-hooks'
import type { PlanRequiredInfo } from '@/api/subscription-hooks'
import { shortPriceLine } from '@/lib/plan-pricing'
import { useBillingInterval } from '@/lib/billing-interval'
import { IntervalToggle } from '@/components/shared/IntervalToggle'
import { planAudience } from '@/lib/plan-audience'
import { Spinner } from '@/components/shared/Spinner'
import { regionalText } from '@/lib/regional-copy'

// What each entitlement key means to a human, and the one-liner that sells
// it. Copy is ours to steer here — the web is not bound by the App Store
// rules that keep the server's 402 detail neutral (CLA-906).
const FEATURE_COPY: Record<string, { name: string; pitch: string }> = {
  booking: {
    name: 'Online booking',
    pitch: 'Patients book and pay deposits themselves — your calendar fills while you treat.',
  },
  store: {
    name: 'Store',
    pitch: 'Sell skincare and aftercare products with payments handled end to end.',
  },
  telehealth: {
    name: 'Telehealth',
    pitch: 'Video consultations and prescriber calls, built into the patient record.',
  },
  analytics: {
    name: 'Analytics',
    get pitch() { return regionalText('See how the clinic is actually running — utilisation, demand, consumption.') },
  },
  custom_templates: {
    name: 'Custom note profiles',
    pitch: 'Shape AI notes to exactly how you like to document.',
  },
  artifacts: {
    name: 'Patient documents',
    pitch: 'Aftercare summaries and GP letters, generated and sent from the consult.',
  },
  team: {
    name: 'Team',
    get pitch() { return regionalText('Add more practitioners and run a multi-practitioner clinic.') },
  },
  model_tier_detailed: {
    name: 'Detailed AI notes',
    pitch: 'Longer, richer clinical notes from the same recording.',
  },
}

/** Sentinel feature key for the write lock, which names no real feature. */
const WRITE_LOCKED = '__write_locked__'

// The Clinic plan is "Practice" in the US (CLA-1829); a getter, so it follows the realm at read time.
const PLAN_LABEL: Record<string, string> = {
  scribe: 'Scribe',
  get clinic() { return regionalText('Clinic') },
}

/**
 * Global upgrade panel (CLA-906). Listens for the `claro:plan-required`
 * event the api client dispatches on any 402 plan_required, and can be
 * opened directly via `openUpgradePanel(feature)` from locked affordances
 * (nav chips) before a request is ever made.
 *
 * Role-aware by design: clinic managers get the checkout CTA; everyone
 * else gets told who can upgrade — never a buy button they can't use.
 */
export function UpgradePanel() {
  const [info, setInfo] = useState<PlanRequiredInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const canManage = useAuthStore((s) => s.canManageClinic)()
  // Only once the panel is actually open (CLA-1170). Like TrialStrip, this
  // mounts on every page but returns null below until a 402 plan_required
  // event (or openUpgradePanel) sets `info` — so an unconditional fetch spent
  // one authenticated read per cold boot on a catalogue almost nobody saw.
  // Safe by this component's own design: the price is documented below as
  // null until the query resolves, and everything reads fine without it.
  const { data: catalogue } = usePlans({ enabled: info !== null })
  const { data: sub } = useSubscription()
  const checkout = useCheckout()
  // CLA-1622: this panel hardcoded 'monthly', so anyone who started here — the
  // trial strip on every page, a locked feature — never saw annual, even after
  // choosing it on the plan card. One stored choice, shared with PlanSettings.
  const [interval, setInterval] = useBillingInterval()

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { feature?: string; required_plan?: string | null; detail?: string; code?: string }
        | undefined
      // A feature gate names its feature. The write lock (CLA-1167) comes
      // from a pipeline plug and names none — it is a state, not a
      // capability — so it is admitted on its code instead. Without this it
      // was silently dropped and a locked clinic saw nothing at all.
      const locked = detail?.code === 'write_locked'
      if (!detail?.feature && !locked) return
      setError(null)
      setInfo({
        feature: locked ? WRITE_LOCKED : (detail.feature as string),
        required_plan: detail.required_plan ?? null,
        detail: detail.detail ?? '',
      })
    }
    window.addEventListener('claro:plan-required', handler)
    return () => window.removeEventListener('claro:plan-required', handler)
  }, [])

  const close = useCallback(() => setInfo(null), [])

  useEffect(() => {
    if (!info) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [info, close])

  if (!info) return null

  const locked = info.feature === WRITE_LOCKED
  const copy = FEATURE_COPY[info.feature] ?? {
    name: 'This feature',
    pitch: regionalText("It isn't part of your clinic's current plan."),
  }
  const targetSlug =
    info.required_plan && PLAN_LABEL[info.required_plan] ? info.required_plan : null
  const targetPlan = targetSlug
    ? (catalogue?.plans ?? []).find((p) => p.slug === targetSlug)
    : null
  const targetLabel = targetSlug ? PLAN_LABEL[targetSlug] : null
  // Null until the plans query resolves, and null for sales-priced plans — the
  // button renders as soon as the event names a plan, so it must read fine with
  // no price at all rather than trailing a bare dash.
  const targetPrice = targetPlan ? shortPriceLine(targetPlan, interval) : null
  // Public plans that can actually be bought. `GET /subscription/plans` is
  // already public-only server-side, so a sales-priced tier (Network) is
  // excluded here by having no price rather than by a hardcoded list.
  const payablePlans = locked
    ? (catalogue?.plans ?? []).filter((p) => shortPriceLine(p, 'monthly'))
    : []
  // The plans a checkout here could buy — what the Annual option's saving is
  // computed from. Empty until the catalogue arrives, and then no toggle shows:
  // a choice with no prices beside it isn't one.
  const offered = locked
    ? payablePlans
    : targetPlan && shortPriceLine(targetPlan, 'monthly')
      ? [targetPlan]
      : []
  const trialing = sub?.status === 'trialing'

  const startCheckoutFor = async (slug: string) => {
    setError(null)
    try {
      const { url } = await checkout.mutateAsync({ plan: slug, interval })
      window.location.assign(url)
    } catch {
      setError('Checkout is unavailable right now — you can also choose a plan in Settings.')
    }
  }

  const startCheckout = async () => {
    if (!targetSlug) return
    setError(null)
    try {
      const { url } = await checkout.mutateAsync({ plan: targetSlug, interval })
      window.location.assign(url)
    } catch {
      setError('Checkout is unavailable right now — you can also upgrade from Settings.')
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${copy.name} requires an upgraded plan`}
    >
      <div className="absolute inset-0 bg-black/40" onClick={close} />
      <div className="relative bg-surface rounded-2xl shadow-xl max-w-sm w-full p-6">
        <button
          className="absolute top-4 right-4 text-ink-faint hover:text-ink"
          onClick={close}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="w-10 h-10 rounded-xl bg-claro-50 flex items-center justify-center mb-4">
          <Lock className="w-5 h-5 text-claro-600" />
        </div>

        {locked ? (
          <>
            <h2 className="text-base font-semibold text-ink mb-1">
              {regionalText('This clinic is read-only')}
            </h2>
            {/* Says what they KEEP first, and specifically. "Read-only" on its
                own reads as "you can't work here any more", which is both
                untrue and the most expensive possible misunderstanding — an
                owner who believes it leaves rather than subscribes. */}
            <p className="text-sm text-ink-secondary mb-2">
              Everything you've recorded is still here and still exportable — notes, patients,
              documents. Nothing has been deleted and nothing will be.
            </p>
            <p className="text-sm text-ink-secondary mb-4">
              What's paused is creating and changing records. A consultation already underway can
              still be finished and signed.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-base font-semibold text-ink mb-1">
              {copy.name}
              {targetLabel ? ` is part of the ${targetLabel} plan` : " isn't in your current plan"}
            </h2>
            <p className="text-sm text-ink-secondary mb-4">{copy.pitch}</p>
          </>
        )}

        {trialing && (
          <p className="text-xs text-ink-secondary mb-4">
            You have full access during your trial — choosing a plan keeps it that way.
          </p>
        )}

        {error && (
          <div className="rounded-lg bg-error-light text-on-error-soft text-xs px-3 py-2 mb-3">{error}</div>
        )}

        {canManage && locked ? (
          /* A locked clinic has lost everything, so there is no single
             feature to pitch and no `required_plan` to send them to. Bouncing
             them to Settings is a navigation, not a call to action, at the
             moment of highest intent — they have just been stopped
             mid-task. Show the real choice with real prices instead, so
             turning the clinic back on is one click.

             Prices come from `shortPriceLine`, never re-derived here: it is
             the one place that knows the CLA-937 base-vs-seat rule, where
             quoting Clinic's A$149 seat price as an entry price would
             understate the real floor of A$349. */
          <div className="space-y-2">
            {offered.length > 0 && (
              <div className="flex justify-center pb-1">
                <IntervalToggle value={interval} onChange={setInterval} plans={offered} />
              </div>
            )}
            {payablePlans.map((p) => (
              <div key={p.slug} className="space-y-1">
                <button
                  className="w-full bg-claro-600 hover:bg-claro-700 text-white text-sm font-medium rounded-lg px-4 py-2.5 flex items-center justify-center gap-2 disabled:bg-primary-disabled disabled:text-on-primary-disabled disabled:cursor-not-allowed"
                  onClick={() => startCheckoutFor(p.slug)}
                  disabled={checkout.isPending}
                >
                  {checkout.isPending && <Spinner className="w-4 h-4" />}
                  {PLAN_LABEL[p.slug] ?? p.display_name}
                  {shortPriceLine(p, interval) ? ` — ${shortPriceLine(p, interval)}` : ''}
                </button>
                {planAudience(p.slug) && (
                  <p className="text-xs text-ink-secondary text-center">{planAudience(p.slug)}</p>
                )}
              </div>
            ))}
            {payablePlans.length === 0 && (
              /* The catalogue is still loading, or every plan is sales-priced.
                 Never leave a locked clinic with no way forward. */
              <Link
                to="/settings/plan"
                onClick={close}
                className="w-full bg-claro-600 hover:bg-claro-700 text-white text-sm font-medium rounded-lg px-4 py-2.5 flex items-center justify-center"
              >
                Choose a plan
              </Link>
            )}
            <button className="w-full text-xs text-ink-faint hover:text-ink py-1" onClick={close}>
              Not now
            </button>
          </div>
        ) : canManage && targetSlug ? (
          <div className="space-y-2">
            {offered.length > 0 && (
              <div className="flex justify-center pb-1">
                <IntervalToggle value={interval} onChange={setInterval} plans={offered} />
              </div>
            )}
            <button
              className="w-full bg-claro-600 hover:bg-claro-700 text-white text-sm font-medium rounded-lg px-4 py-2.5 flex items-center justify-center gap-2"
              onClick={startCheckout}
              disabled={checkout.isPending}
            >
              {checkout.isPending && <Spinner className="w-4 h-4" />}
              {/* CLA-961: this used to read "from $<seat price>/practitioner/mo",
                  which for Clinic quoted $149 — the ADDITIONAL-practitioner
                  price — as if it were the entry price, when the least any
                  clinic can pay for Clinic is $349. shortPriceLine is the one
                  place that knows base-vs-seat. */}
              Upgrade to {targetLabel}
              {targetPrice ? ` — ${targetPrice}` : ''}
            </button>
            <button
              className="w-full text-xs text-ink-faint hover:text-ink py-1"
              onClick={close}
            >
              Not now
            </button>
          </div>
        ) : canManage ? (
          /* A manager with no resolvable target plan still needs a way forward —
             they just can't be sent straight to checkout, because we don't know
             which plan to mint. Settings has the chooser. (CLA-961) */
          <Link
            to="/settings/plan"
            onClick={close}
            className="w-full bg-claro-600 hover:bg-claro-700 text-white text-sm font-medium rounded-lg px-4 py-2.5 flex items-center justify-center"
          >
            Choose a plan
          </Link>
        ) : (
          <p className="text-xs text-ink-secondary">
            {targetLabel
              ? regionalText('Available on the {targetLabel} plan — your clinic owner can upgrade in Settings.', { targetLabel })
              : regionalText('Your clinic owner can change the plan in Settings.')}
          </p>
        )}
      </div>
    </div>
  )
}
